import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SAVED_VIEWS } from '../constants/queryKeys';
import * as api from '../services/savedViewsApi';
import {
  CreateSavedViewRequest,
  TreeFilterOptions,
  UpdateSavedViewRequest,
} from '../types/savedViewsTypes';
import useGraphqlQuery from './useGraphqlQuery';
import useSelectedEnv from './useSelectedEnv';

type Props =
  | {
      savedViewId?: string;
      enabled?: boolean;
    }
  | undefined;

const useSavedViews = ({ savedViewId, enabled = true }: Props = {}) => {
  const queryClient = useQueryClient();
  const envid = useSelectedEnv();

  const {
    data: savedViewsData,
    error: savedViewsError,
    isLoading: isSavedViewsLoading,
    isFetching: isSavedViewsFetching,
  } = useQuery({
    queryKey: [SAVED_VIEWS, envid],
    queryFn: () => api.fetchSavedViews(envid!),
    enabled: !!envid && enabled,
  });

  const {
    data: savedViewData,
    error: savedViewError,
    isLoading: isSavedViewLoading,
    isFetching: isSavedViewFetching,
  } = useQuery({
    queryKey: [SAVED_VIEWS, envid, savedViewId],
    queryFn: () => api.fetchSavedView(envid!, savedViewId!),
    enabled: !!envid && !!savedViewId && enabled,
  });

  const {
    mutate: createSavedView,
    mutateAsync: createSavedViewAsync,
    error: createError,
    isLoading: isCreating,
  } = useMutation({
    mutationFn: (data: CreateSavedViewRequest) =>
      api.createSavedView(envid!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_VIEWS, envid],
      });
    },
  });

  const {
    mutate: updateSavedView,
    mutateAsync: updateSavedViewAsync,
    error: updateError,
    isLoading: isUpdating,
  } = useMutation({
    mutationFn: ({
      savedViewId,
      data,
    }: {
      savedViewId: string;
      data: UpdateSavedViewRequest;
    }) => api.updateSavedView(envid!, savedViewId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_VIEWS, envid],
      });
      queryClient.invalidateQueries({
        queryKey: [SAVED_VIEWS, envid, variables.savedViewId],
      });
    },
  });

  const {
    mutate: deleteSavedView,
    mutateAsync: deleteSavedViewAsync,
    error: deleteError,
    isLoading: isDeleting,
  } = useMutation({
    mutationFn: (savedViewId: string) =>
      api.deleteSavedView(envid!, savedViewId),
    onSuccess: (_, savedViewId) => {
      queryClient.invalidateQueries({
        queryKey: [SAVED_VIEWS, envid],
      });
      queryClient.removeQueries({
        queryKey: [SAVED_VIEWS, envid, savedViewId],
      });
    },
  });

  const {
    data: filterOptionsData,
    error: filterOptionsError,
    isLoading: isFilterOptionsLoading,
    isFetching: isFilterOptionsFetching,
  } = useGraphqlQuery<{ treeFilterOptions: TreeFilterOptions }>({
    queryKey: [SAVED_VIEWS, 'filter-options', envid],
    query: api.getTreeFilterOptionsQuery,
    enabled: !!envid && enabled,
  });

  return {
    savedViews: {
      data: savedViewsData,
      error: savedViewsError,
      isLoading: isSavedViewsLoading,
      isFetching: isSavedViewsFetching,
    },
    savedView: {
      data: savedViewData,
      error: savedViewError,
      isLoading: isSavedViewLoading,
      isFetching: isSavedViewFetching,
    },
    filterOptions: {
      data: filterOptionsData?.treeFilterOptions,
      error: filterOptionsError,
      isLoading: isFilterOptionsLoading,
      isFetching: isFilterOptionsFetching,
    },
    create: {
      mutate: createSavedView,
      mutateAsync: createSavedViewAsync,
      error: createError,
      isLoading: isCreating,
    },
    update: {
      mutate: updateSavedView,
      mutateAsync: updateSavedViewAsync,
      error: updateError,
      isLoading: isUpdating,
    },
    delete: {
      mutate: deleteSavedView,
      mutateAsync: deleteSavedViewAsync,
      error: deleteError,
      isLoading: isDeleting,
    },
  };
};

export default useSavedViews;
